<template>
  <b-row>
    <b-col v-if="!loading" cols="12" lg="12">
      <b-card header="Informações" no-body>
        <b-card-body>
          <fieldset class="pb-3">
            <legend hidden>Informações da Demanda</legend>
            <b-row>
              <b-col cols="6">
                <h5>
                  Protocolo: <strong>{{ qas.protocol }}</strong>
                </h5>
                <h5>Data de Recebimento: {{ qas.createdAt | datetime }}</h5>
                <h5>Portal de Recebimento: /{{ qas.portal.slug }}</h5>
              </b-col>
              <b-col>
                <h5>
                  Status:
                  <b-badge :variant="getBadgeColor(qas.status)"
                    >{{ getStatusText(qas.status) }}
                  </b-badge>
                </h5>
                <h5 v-if="qas.status === QaSStatus.CONCLUDED">
                  Data da Resposta: {{ qas.updatedAt | datetime }}
                </h5>
              </b-col>
            </b-row>
          </fieldset>
          <fieldset v-if="masterUser" class="pb-3">
            <legend v-if="qas.anonymous">Dúvida/Sugestão Anônima</legend>
            <legend v-else>Pessoa</legend>
            <template v-if="!qas.anonymous || masterUser">
              <b-row>
                <b-col cols="4">
                  <label>Nome:</label> {{ qas.person.fullName | noInformation }}
                </b-col>
                <b-col cols="4">
                  <label>CPF:</label> {{ qas.person.cpf | noInformation }}
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="4">
                  <label>Telefone:</label>
                  {{ qas.person.phone | noInformation }}
                </b-col>
                <b-col cols="4">
                  <label>Email:</label> {{ qas.person.email | noInformation }}
                </b-col>
              </b-row>
            </template>
            <br />
            <h5>Relação com a {{ qas.company.fantasyName }}</h5>
            <b-row>
              <b-col cols="4">
                {{ qas.relation | noInformation }}
              </b-col>
            </b-row>
          </fieldset>
          <b-row class="pb-3">
            <b-col md="12">
              <h5>Descrição</h5>
              <div>{{ qas.text }}</div>
            </b-col>
          </b-row>
          <b-row v-if="qas.files.length > 0">
            <b-col md="12">
              <h5>Anexos</h5>
              <b-list-group>
                <b-list-group-item
                  v-for="(file, idx) in qas.files"
                  :key="idx"
                  class="d-flex justify-content-between align-items-center"
                >
                  {{ file.name }}
                  <b-button @click="downloadFile(file)"
                    ><i class="fa fa-download" aria-hidden="true"></i>
                  </b-button>
                </b-list-group-item>
              </b-list-group>
            </b-col>
          </b-row>
          <hr />
          <b-row class="pb-3">
            <b-col cols="3">
              <b-form-group no-label>
                <template #label><h5>Classificação</h5></template>
                <b-form-select
                  v-model="qas.classification"
                  plain
                  :disabled="qas.status === QaSStatus.CONCLUDED || !canChange"
                  :options="classificationOpts"
                  aria-describedby="invalidClassification"
                  :state="validClassificationState"
                  @input="validateFields"
                />
                <b-form-invalid-feedback id="invalidClassification">
                  {{ invalidClassificationInfo }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <h5>
                Resposta
                <small v-if="qas.responseUser">
                  (Por:
                  <template v-if="companyUsers[qas.responseUser]">
                    <i class="fa fa-user fa-sm" aria-hidden="true" />
                    {{ companyUsers[qas.responseUser].name }}
                    {{ companyUsers[qas.responseUser].surname }}
                  </template>
                  <template v-else-if="managedByCompanyUsers[qas.responseUser]">
                    <i class="fa fa-user-tie fa-sm" aria-hidden="true" />
                    {{ managedByCompanyUsers[qas.responseUser].name }}
                    {{ managedByCompanyUsers[qas.responseUser].surname }}
                  </template>
                  <template v-else-if="conformitaUsers[qas.responseUser]">
                    <i
                      class="fa fa-user-tie text-info fa-sm"
                      aria-hidden="true"
                    />
                    {{ conformitaUsers[qas.responseUser].name }}
                    {{ conformitaUsers[qas.responseUser].surname }}
                  </template>
                  )
                </small>
              </h5>
              <b-form-group>
                <b-form-textarea
                  v-model="qas.response"
                  :disabled="qas.status === QaSStatus.CONCLUDED || !canChange"
                  :rows="5"
                  aria-describedby="invalidResponse"
                  name="value"
                  :state="validResponseState"
                  @input="validateFields"
                />
                <b-form-invalid-feedback id="invalidResponse">
                  {{ invalidResponseInfo }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-body>
        <b-card-footer>
          <b-button
            variant="primary"
            class="px-4"
            :disabled="
              loading || qas.status === QaSStatus.CONCLUDED || !canChange
            "
            @click="onSaveClicked"
          >
            Salvar
          </b-button>
          <b-button class="px-4 ml-1" :disabled="loading" @click="goBack"
            >Cancelar</b-button
          >
        </b-card-footer>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import { getQaS, setQaSResponse } from "../../api/qas";
import { getSystemUsers } from "../../api/user";
import { getCompanyUsers } from "../../api/company";
import { getStatusText, getBadgeColor } from "../complaints/utils";
import { mapGetters } from "vuex";
import QaSStatus from "../../../../backend/models/QaSStatus.ts";

import { mask } from "vue-the-mask";

export default {
  name: "QaS",
  directives: { mask },
  props: {
    id: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      QaSStatus,
      loading: false,
      qas: {},
      companyUsers: {},
      managedByCompanyUsers: {},
      conformitaUsers: {},
      invalidResponseInfo: "",
      validResponseState: null,
      invalidClassificationInfo: "",
      validClassificationState: null,
      classificationOpts: [
        { value: 1, text: "Dúvida" },
        { value: 2, text: "Sugestão" },
        { value: 3, text: "Improcedente" },
      ],
    };
  },
  computed: {
    ...mapGetters(["profile", "permissions"]),
    masterUser() {
      const role = this.profile.role;
      return role === "root" || role === "company-root";
    },
    conformitaUser() {
      return !!this.conformitaUsers[this.profile._id];
    },
    isComplaintAdmin() {
      return this.profile._id === this.qas.committee.admin;
    },
    canChange() {
      const role = this.profile.role;
      if (parseInt(role) === role) {
        const permissions = this.permissions;
        return (
          (permissions.qas && permissions.qas.create) || permissions.qas.edit
        );
      }
      return true;
    },
  },
  async created() {
    this.loading = true;
    const response = await getQaS(this.id);
    const qas = response.data;
    this.qas = qas;

    let resp = await getCompanyUsers(qas.company._id);
    this.companyUsers = resp.data.reduce((obj, user) => {
      obj[user["_id"]] = user;
      return obj;
    }, {});

    if (qas.company.managedBy) {
      let managedCompanyUsers = (
        await getCompanyUsers(qas.company.managedBy, true)
      ).data;
      this.managedByCompanyUsers = managedCompanyUsers.reduce((obj, user) => {
        obj[user["_id"]] = user;
        return obj;
      }, {});
    }

    resp = await getSystemUsers();
    this.conformitaUsers = resp.data.reduce((obj, user) => {
      obj[user["_id"]] = user;
      return obj;
    }, {});

    this.loading = false;
  },
  methods: {
    getStatusText,
    getBadgeColor,
    downloadFile(file) {
      const link = `${window.location.origin}/api/qas/${this.qas._id}/files/${file._id}`;
      const a = document.createElement("a");
      a.setAttribute("download", "");
      a.setAttribute("href", link);
      a.setAttribute("target", "_blank");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    validateFields() {
      this.validResponseState = true;
      this.validClassificationState = true;
      if (!this.qas.response || !this.qas.response.trim()) {
        this.validResponseState = false;
        this.invalidResponseInfo = "A resposta deve ser preenchida!";
      }
      if (!this.qas.classification) {
        this.validClassificationState = false;
        this.invalidClassificationInfo = "A classificação deve ser preenchida!";
      }

      return this.validResponseState && this.validClassificationState;
    },

    async onSaveClicked() {
      if (!this.validateFields()) {
        return;
      }

      try {
        const response = await setQaSResponse(
          this.qas._id,
          this.qas.response,
          this.qas.classification
        );
        this.qas = response.data;
        this.validResponseState = null;
        this.validClassificationState = null;
        this.$bvToast.toast("Resposta enviada com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        if (error.response.status === 403 || error.response.status === 404) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },
    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>
